import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Trophy, AlertCircle, Medal, Target } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '@/services/api';

export default function GuessingPage({ user }) {
  const [competition, setCompetition] = useState(null);
  const [endedCompetition, setEndedCompetition] = useState(null);
  const [hunt, setHunt] = useState(null);
  const [guesses, setGuesses] = useState([]);
  const [myGuess, setMyGuess] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchCompetition();
  }, []);

  const fetchCompetition = async () => {
    try {
      // First check for active competition
      const compResponse = await api.get('/competitions/active');
      if (compResponse.data) {
        setCompetition(compResponse.data);
        
        const huntResponse = await api.get(`/hunts/${compResponse.data.hunt_id}`);
        setHunt(huntResponse.data);
        
        const guessesResponse = await api.get(`/guesses/hunt/${compResponse.data.hunt_id}`);
        setGuesses(guessesResponse.data);
      } else {
        // No active competition, fetch the latest ended one
        try {
          const endedResponse = await api.get('/competitions/latest-ended');
          if (endedResponse.data) {
            setEndedCompetition(endedResponse.data);
          }
        } catch (e) {
          console.error('Failed to fetch ended competition:', e);
        }
      }
    } catch (error) {
      console.error('Failed to fetch competition:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitGuess = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!user) {
      setError('Please login to submit a guess');
      return;
    }

    try {
      await api.post('/guesses', { guess_amount: parseFloat(myGuess) });
      setSuccess('Guess submitted successfully!');
      setMyGuess('');
      fetchCompetition();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to submit guess');
    }
  };

  if (loading) {
    return <div className="container mx-auto px-4 pt-32 pb-8">Loading...</div>;
  }

  // Show ended competition results when no active competition
  if (!competition && endedCompetition) {
    const winner = endedCompetition.winner_username ? {
      username: endedCompetition.winner_username,
      guess: endedCompetition.winner_guess,
      difference: endedCompetition.winner_difference
    } : null;
    const allGuesses = endedCompetition.guesses || [];

    return (
      <div className="container mx-auto px-4 pt-32 pb-8">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-4xl font-bold text-purple-500 mb-8" data-testid="guessing-page-title">End Balance Guessing</h1>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Competition Results Card */}
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-purple-400" />
                  Last Competition Results
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-400">Status</p>
                    <p className="text-xl font-bold text-red-500">ENDED</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Final Balance</p>
                    <p className="text-3xl font-bold text-yellow-400" data-testid="final-balance">
                      C${endedCompetition.final_balance?.toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Total Guesses</p>
                    <p className="text-2xl font-bold">{endedCompetition.total_guesses || allGuesses.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Winner Card */}
            {winner ? (
              <Card className="bg-gradient-to-br from-yellow-900 to-gray-900 border-yellow-700" data-testid="winner-card">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Trophy className="h-6 w-6 text-yellow-500" />
                    <span>🏆 Winner!</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-gray-400">Username</p>
                      <p className="text-2xl font-bold text-yellow-400" data-testid="winner-username">
                        {winner.username}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">Winning Guess</p>
                      <p className="text-3xl font-bold text-white" data-testid="winner-guess">
                        C${winner.guess?.toLocaleString()}
                      </p>
                    </div>
                    <div className="pt-2 border-t border-yellow-700/50">
                      <p className="text-sm text-gray-400">How Close They Were</p>
                      <p className="text-xl font-semibold text-green-400" data-testid="winner-difference">
                        Only C${winner.difference?.toFixed(2)} off! 🎯
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-gray-900 border-gray-800">
                <CardContent className="py-12">
                  <p className="text-center text-gray-400">No guesses were submitted for the last competition.</p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* All Guesses Sorted by Closest */}
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Medal className="h-5 w-5 text-purple-400" />
                All Guesses (Sorted by Closest)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {allGuesses.length === 0 ? (
                <p className="text-center text-gray-400 py-8">No guesses were submitted.</p>
              ) : (
                <div className="space-y-2" data-testid="all-guesses-list">
                  {allGuesses.map((guess, idx) => {
                    const isWinner = idx === 0;
                    const isTop3 = idx < 3;
                    return (
                      <div
                        key={guess.guess_id || idx}
                        className={`flex justify-between items-center p-4 rounded-lg ${
                          isWinner 
                            ? 'bg-gradient-to-r from-yellow-900/50 to-yellow-700/30 border border-yellow-600' 
                            : isTop3 
                              ? 'bg-gray-800/80 border border-gray-700' 
                              : 'bg-gray-800'
                        }`}
                        data-testid={`guess-row-${idx}`}
                      >
                        <div className="flex items-center space-x-4">
                          <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                            isWinner 
                              ? 'bg-yellow-500 text-black' 
                              : idx === 1 
                                ? 'bg-gray-400 text-black' 
                                : idx === 2 
                                  ? 'bg-amber-700 text-white' 
                                  : 'bg-gray-700 text-gray-300'
                          }`}>
                            {idx + 1}
                          </span>
                          <div>
                            <p className={`font-semibold ${isWinner ? 'text-yellow-400' : ''}`}>
                              {guess.discord_username || 'Unknown User'}
                            </p>
                            {guess.timestamp && (
                              <p className="text-xs text-gray-500">
                                {new Date(guess.timestamp).toLocaleString()}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`text-xl font-bold ${isWinner ? 'text-yellow-400' : 'text-white'}`}>
                            C${guess.guess_amount?.toLocaleString()}
                          </p>
                          <p className={`text-sm ${isWinner ? 'text-green-400' : 'text-gray-400'}`}>
                            C${guess.difference?.toFixed(2)} off
                          </p>
                          {isWinner && (
                            <p className="text-xs text-yellow-500 mt-1">🏆 Winner!</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Info Alert */}
          <div className="mt-6">
            <Alert className="bg-gray-900 border-gray-700">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                No active competition at the moment. Check back later for the next guessing competition!
              </AlertDescription>
            </Alert>
          </div>

          {user?.is_admin && (
            <div className="text-center mt-4">
              <Button onClick={() => navigate('/admin')} data-testid="admin-start-competition-btn">
                Start New Competition
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // No competition at all
  if (!competition) {
    return (
      <div className="container mx-auto px-4 pt-32 pb-8">
        <Card className="bg-gray-900 border-gray-800 max-w-2xl mx-auto">
          <CardContent className="py-12">
            <p className="text-center text-gray-400 text-xl">
              No active guessing competition at the moment.
            </p>
            {user?.is_admin && (
              <div className="text-center mt-4">
                <Button onClick={() => navigate('/admin')}>
                  Start a Competition
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Active competition view
  const isEnded = competition.status === 'ended';
  const isPaused = competition.status === 'paused';
  const userHasGuessed = guesses.some((g) => g.user_discord_id === user?.discord_id);

  return (
    <div className="container mx-auto px-4 pt-32 pb-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-4xl font-bold text-purple-500 mb-8" data-testid="guessing-page-title">End Balance Guessing</h1>

        {isPaused && (
          <Alert className="mb-6 bg-yellow-900/30 border-yellow-600" data-testid="guessing-paused-alert">
            <AlertCircle className="h-4 w-4 text-yellow-400" />
            <AlertDescription className="text-yellow-300 font-semibold">
              Guessing is currently paused. Stay tuned - it will be back soon!
            </AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <CardTitle>Competition Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-400">Status</p>
                  <p className="text-2xl font-bold">
                    {isEnded ? (
                      <span className="text-red-500">ENDED</span>
                    ) : isPaused ? (
                      <span className="text-yellow-500">PAUSED</span>
                    ) : (
                      <span className="text-green-500">ACTIVE</span>
                    )}
                  </p>
                </div>
                {hunt && (
                  <div>
                    <p className="text-sm text-gray-400">Hunt</p>
                    <p className="text-lg">#{hunt.hunt_id.slice(0, 8)}</p>
                    <p className="text-sm text-gray-400">Start Balance: C${hunt.start_balance.toLocaleString()}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-gray-400">Total Guesses</p>
                  <p className="text-2xl font-bold">{guesses.length}</p>
                </div>
                {isEnded && competition.final_balance && (
                  <div>
                    <p className="text-sm text-gray-400">Final Balance</p>
                    <p className="text-3xl font-bold text-purple-500">
                      C${competition.final_balance.toLocaleString()}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {!isEnded && !isPaused && (
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <CardTitle>Submit Your Guess</CardTitle>
              </CardHeader>
              <CardContent>
                {!user ? (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      Please login with Discord to submit a guess.
                    </AlertDescription>
                  </Alert>
                ) : userHasGuessed ? (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      You've already submitted a guess for this competition!
                    </AlertDescription>
                  </Alert>
                ) : (
                  <form onSubmit={handleSubmitGuess} className="space-y-4">
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">
                        Your guess for the ending balance:
                      </label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Enter amount (e.g., 5000.00)"
                        value={myGuess}
                        onChange={(e) => setMyGuess(e.target.value)}
                        required
                        className="bg-gray-800 border-gray-700"
                      />
                    </div>
                    {error && (
                      <Alert variant="destructive">
                        <AlertDescription>{error}</AlertDescription>
                      </Alert>
                    )}
                    {success && (
                      <Alert className="bg-green-900 border-green-700">
                        <AlertDescription>{success}</AlertDescription>
                      </Alert>
                    )}
                    <Button type="submit" className="w-full" data-testid="submit-guess-button">
                      Submit Guess
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          )}

          {isEnded && competition.winner_discord_id && (
            <Card className="bg-gradient-to-br from-yellow-900 to-gray-900 border-yellow-700">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Trophy className="h-6 w-6 text-yellow-500" />
                  <span>Winner!</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <p className="text-sm text-gray-400">Winning Guess</p>
                  <p className="text-3xl font-bold text-yellow-500">
                    C${competition.winner_guess?.toLocaleString()}
                  </p>
                  <p className="text-sm text-gray-400">Difference</p>
                  <p className="text-xl">
                    C${Math.abs(competition.final_balance - competition.winner_guess).toFixed(2)}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <Card className="bg-gray-900 border-gray-800">
          <CardHeader>
            <CardTitle>All Guesses</CardTitle>
          </CardHeader>
          <CardContent>
            {guesses.length === 0 ? (
              <p className="text-center text-gray-400 py-8">No guesses yet. Be the first!</p>
            ) : (
              <div className="space-y-2">
                {guesses.map((guess, idx) => {
                  const isWinner = isEnded && guess.user_discord_id === competition.winner_discord_id;
                  return (
                    <div
                      key={guess.guess_id}
                      className={`flex justify-between items-center p-4 rounded-lg ${
                        isWinner ? 'bg-yellow-900 border border-yellow-700' : 'bg-gray-800'
                      }`}
                    >
                      <div className="flex items-center space-x-4">
                        <span className="text-gray-400">#{idx + 1}</span>
                        <div>
                          <p className="font-semibold">{guess.discord_username || 'Unknown User'}</p>
                          <p className="text-sm text-gray-400">
                            {new Date(guess.timestamp).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold">C${guess.guess_amount.toLocaleString()}</p>
                        {isWinner && (
                          <p className="text-sm text-yellow-500">🏆 Winner!</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
